import React, { useCallback, useMemo } from 'react';
import type { Campaign } from 'c-sdk';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Checkbox } from 'c-components';
import UpdatedByUserCaption from 'c-components/UpdatedByUserCaption';
import { useUpdateEntityData } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import { useUserPermissions } from 'c-auth-module/Hooks';
import type { TranslationPath } from 'c-translation';
import { useCommonTranslation } from 'c-translation';

type Props = {
    campaign: Campaign;
    value: boolean;
    dateKey: keyof Campaign;
    userIdKey: keyof Campaign;
    label: TranslationPath;
    generateSaveData: (campaign: Campaign, newState: boolean) => Campaign;
    action?: React.ReactNode;
};

const CampaignUserEditedBooleanCheckbox: React.FC<Props> = ({
    value,
    dateKey,
    userIdKey,
    campaign,
    label,
    generateSaveData,
    action,
}) => {
    const { update, getUpdatingById } = useUpdateEntityData<Campaign>('Campaign');
    const loading = useMemo(
        () => getUpdatingById(campaign.id).state === NetworkRequestState.InProgress,
        [campaign.id, getUpdatingById],
    );

    const onChange = useCallback(() => {
        update(campaign.id, generateSaveData(campaign, !value));
    }, [update, campaign, value, generateSaveData]);

    const { hasAdminPermission } = useUserPermissions();

    const showCaption = campaign?.[userIdKey] != null && value === true;

    return (
        <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Checkbox
                    disabled={loading || !hasAdminPermission}
                    checked={value}
                    label={
                        <Typography variant="caption" noWrap>
                            {useCommonTranslation(label)}
                        </Typography>
                    }
                    onChange={onChange}
                    isBoolean
                    checkedIcon={loading ? <CircularProgress size={15} /> : undefined}
                    icon={loading ? <CircularProgress size={15} /> : undefined}
                />
                {action}
            </Box>
            {showCaption && (
                <UpdatedByUserCaption
                    justifyContent="flex-start"
                    userId={campaign[userIdKey] as number}
                    updatedAt={campaign[dateKey] as string}
                />
            )}
        </Box>
    );
};

export default CampaignUserEditedBooleanCheckbox;
